import { ApiError, type MeContext, type MeStore } from "@khazzin/api-client";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from "react";
import { useApi } from "../../api";
import { activeStore } from "./activeStore";
import { pickStore, rememberedStore, rememberStore } from "./sections";

/**
 * The store the portal is working in (EP10-S02).
 *
 * **One store at a time, on every request.** The API client reads
 * {@link activeStore} for its `X-Store-Id` header on each call, so a switch
 * changes the next request without rebuilding anything. A switch also drops
 * every cached answer except the caller's own context: a row fetched under the
 * previous store must never be on screen under the new one.
 */

export const ME_CONTEXT_KEY = ["me", "context"] as const;

interface StoreScope {
	context: MeContext | undefined;
	loading: boolean;
	/** `/me/context` failed for a reason other than the session being over. */
	failed: boolean;
	retry: () => void;
	/**
	 * A request anywhere in the portal was answered 401 (ADJ-M03-4): the token
	 * is revoked or the session expired, and every screen would otherwise show
	 * its own error for what is one fact.
	 */
	sessionOver: boolean;
	stores: MeStore[];
	store: MeStore | null;
	switchStore: (storeId: string) => void;
}

const ScopeContext = createContext<StoreScope | null>(null);

export function useStoreScope(): StoreScope {
	const scope = useContext(ScopeContext);
	if (scope === null) {
		throw new Error("useStoreScope must be used inside <StoreScopeProvider>");
	}
	return scope;
}

export function StoreScopeProvider({
	enabled,
	children,
}: {
	enabled: boolean;
	children: ReactNode;
}) {
	const api = useApi();
	const queryClient = useQueryClient();
	const [chosen, setChosen] = useState<string | null>(rememberedStore);
	const context = useQuery({
		queryKey: ME_CONTEXT_KEY,
		queryFn: ({ signal }) => api.getMeContext({ signal }),
		enabled,
	});
	const sessionOver = useSessionOver();
	const { refetch } = context;
	const stores = context.data?.stores ?? [];
	// A Login as Store session is one store's (EP01-S16), whatever was
	// remembered in this browser.
	const store = pickStore(stores, context.data?.impersonation?.storeId ?? chosen);
	// Set during render, before any child's query runs with it.
	activeStore.current = store?.storeId ?? null;

	const switchStore = useCallback(
		(storeId: string) => {
			if (storeId === activeStore.current) {
				return;
			}
			rememberStore(storeId);
			activeStore.current = storeId;
			queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== "me" });
			setChosen(storeId);
		},
		[queryClient],
	);

	const value = useMemo<StoreScope>(
		() => ({
			context: context.data,
			loading: context.isPending && enabled,
			failed: context.isError,
			retry: () => void refetch(),
			sessionOver,
			stores,
			store,
			switchStore,
		}),
		[
			context.data,
			context.isPending,
			context.isError,
			refetch,
			enabled,
			sessionOver,
			stores,
			store,
			switchStore,
		],
	);
	return <ScopeContext.Provider value={value}>{children}</ScopeContext.Provider>;
}

/** Whether a thrown value is the server saying the session is over. */
export function isSessionOver(error: unknown): boolean {
	return error instanceof ApiError && error.status === 401;
}

/**
 * Watches every query and mutation the portal runs for a 401 (ADJ-M03-4).
 * Once seen it stays seen: a session does not come back by itself, and the
 * shell's "Sign in again" is the way out.
 */
function useSessionOver(): boolean {
	const queryClient = useQueryClient();
	const [over, setOver] = useState(false);
	useEffect(() => {
		const flag = (error: unknown) => {
			if (isSessionOver(error)) {
				setOver(true);
			}
		};
		const offQueries = queryClient.getQueryCache().subscribe((event) => {
			if (event.type === "updated" && event.action.type === "error") {
				flag(event.action.error);
			}
		});
		const offMutations = queryClient.getMutationCache().subscribe((event) => {
			if (event.type === "updated" && event.action.type === "error") {
				flag(event.action.error);
			}
		});
		return () => {
			offQueries();
			offMutations();
		};
	}, [queryClient]);
	return over;
}
